import { useCallback, useEffect, useRef, useState } from "react";

const isAbort = (err) => err?.name === "AbortError" || err?.cause?.name === "AbortError";

/**
 * Minimal fetch-state hook: calls `fetcher({ signal })` whenever `key` changes,
 * aborts stale requests, and keeps the previous data while reloading.
 */
export function useApiQuery(fetcher, key) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const controllerRef = useRef(null);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const refetch = useCallback(async () => {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setLoading(true);
    setError("");
    try {
      const result = await fetcherRef.current({ signal: controller.signal });
      if (!controller.signal.aborted) setData(result);
    } catch (err) {
      if (isAbort(err) || controller.signal.aborted) return;
      setError(err?.message || "Request failed.");
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, []);

  useEffect(() => {
    refetch();
    return () => controllerRef.current?.abort();
  }, [key, refetch]);

  return { data, loading, error, refetch };
}
